import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { CourseEditTemplate } from "@shared/models/analytics/course"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardStackAttributes } from "@shared/models/card-stack"
import type { Course, CourseCalendarDateDefault } from "@shared/models/course"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "../../action-doc-builder"
import { isCourseDateDefault } from "cc-frontend/utils/filter-dates/course"
import { filter, flatten, map, partial } from "lodash"
import { compact, forEach } from "lodash-es"
import type { Narrative } from "../create-narrative"
import { COURSE_DATE_SAVE_AS_TEMPLATE } from "./calendar/COURSE_DATE_SAVE_AS_TEMPLATE"
import COURSE_ROTATION_SET_TEMPLATE_ID from "./calendar/COURSE_ROTATION_SET_TEMPLATE_ID"
import defaultCourseActivity from "./default-course-activity"
import defaultCourseContext from "./default-course-context"
import defaultCourseModels from "./default-course-models"

interface Payload {
  course: Course
  planbookId: string
  cardStackId: string | null
  shouldCreateNewTemplate?: boolean
  newTemplateAttrs?: CardStackAttributes
  rotationCalendar: RotationCalendar
}

export function prepareCourseAssignLessonTemplateToWeek(payload: Payload): PreparedAction {
  return {
    name: COURSE_ASSIGN_LESSON_TEMPLATE_TO_WEEK.name,
    payload: payload,
    patches: COURSE_ASSIGN_LESSON_TEMPLATE_TO_WEEK.patches(payload),
    undoPatches: COURSE_ASSIGN_LESSON_TEMPLATE_TO_WEEK.undoPatches(payload),
    narrative: partial(COURSE_ASSIGN_LESSON_TEMPLATE_TO_WEEK.narrative, payload),
  }
}

export const COURSE_ASSIGN_LESSON_TEMPLATE_TO_WEEK = {
  name: "COURSE_ASSIGN_LESSON_TEMPLATE_TO_WEEK",

  patches(payload: Payload): Array<PatchModel> {
    // check if there are date defaults
    let defaultDates = filter(payload.course.attributes.calendar.dates, isCourseDateDefault)
    let patches: Array<PatchModel> = []
    if (defaultDates.length === 0) {
      let semester = payload.rotationCalendar.attributes.semesters[0]
      patches = map(payload.rotationCalendar.attributes.rotations, (rotation) => {
        return Patch.create("course", payload.course.id)
          .push("attributes.calendar.dates", {
            id: rotation.id,
            type: "course-date-default",
            attributes: {
              rotationId: rotation.id,
              semesterId: semester.id,
              cardStackId: payload.cardStackId,
              time: null,
            },
          })
          .inc("attributes.calendar.version", 1).patch
      })
      // patches.concat([
      //   Patch.create("course", payload.course.id).inc("attributes.calendar.version", 1).patch,
      // ])
      // console.log("patches", patches)
    } else {
      patches = flatten(
        map(
          filter(
            payload.course.attributes.calendar.dates,
            (date) => date.type === "course-date-default"
          ),
          (courseDate: CourseCalendarDateDefault): Array<PatchModel> => {
            return COURSE_ROTATION_SET_TEMPLATE_ID.patches({
              courseId: payload.course.id,
              rotationId: courseDate.attributes.rotationId,
              semesterId: courseDate.attributes.semesterId,
              planbookId: payload.planbookId,
              dateId: courseDate.id,
              cardStackId: payload.cardStackId,
              currentTemplateId: courseDate.attributes.cardStackId,
            })
          }
        )
      )
    }

    if (
      payload.shouldCreateNewTemplate === true &&
      payload.newTemplateAttrs !== undefined &&
      payload.cardStackId
    ) {
      forEach(
        COURSE_DATE_SAVE_AS_TEMPLATE.patches({
          newId: payload.cardStackId,
          planbookId: payload.planbookId,
          cardStackAttributes: payload.newTemplateAttrs,
        }),
        (patch) => patches.unshift(patch)
      )
    }
    return patches
  },

  undoPatches(payload: Payload): Array<PatchModel> {
    return flatten(
      map(
        filter(
          payload.course.attributes.calendar.dates,
          (date) => date.type === "course-date-default"
        ),
        (courseDate: CourseCalendarDateDefault): Array<PatchModel> => {
          return COURSE_ROTATION_SET_TEMPLATE_ID.undoPatches({
            courseId: payload.course.id,
            rotationId: courseDate.attributes.rotationId,
            semesterId: courseDate.attributes.semesterId,
            planbookId: payload.planbookId,
            dateId: courseDate.id,
            cardStackId: payload.cardStackId,
            currentTemplateId: courseDate.attributes.cardStackId,
          })
        }
      )
    )
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultCourseModels(payload, findFn, userId)
    let cardStackIdAndAncestorIds = compact([
      payload.planbookId,
      payload.course.id,
      payload.cardStackId,
    ])
    let newEvent: CourseEditTemplate = {
      ...createBlankEvent(),
      root_id: payload.planbookId,
      root_type: "planbook",
      date_plan_scheduled_for: null,

      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

      primary_object_type: "course",
      primary_object_id: payload.course.id,
      primary_object_specifier: "template_assignment",
      primary_object_id_and_ancestor_ids: cardStackIdAndAncestorIds,

      adverbial_verb: "editing",

      adverbial_object_type: "rotation",
      adverbial_object_id: payload.rotationCalendar.id,
      adverbial_object_specifier: "all",
      adverbial_object_id_and_ancestor_ids: cardStackIdAndAncestorIds,
    }

    return {
      title: "Assigned Template",
      titleForAnalytics: "Course - Assigned Template to Week",
      description: `Added a template to your ${payload.course.attributes.title} class`,
      context: defaultCourseContext(payload, models),
      activity: defaultCourseActivity(payload, models),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default COURSE_ASSIGN_LESSON_TEMPLATE_TO_WEEK
